import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '../constants';
import {
  fetchInstantSurveys,
  fetchInstantSurveysData,
  createInstantSurvey, updateInstantSurvey,
} from '../api';

const initialState = {
  // Assessment instruments for selector
  assessmentInstruments: [],

  // Instant Surveys from API
  instantSurveys: [],
  surveysStatus: REQUEST_STATUS.idle,
  instantSurveysStatus: REQUEST_STATUS.idle,
  instantSurveysError: null,

  // Selected assessment
  selectedInstrument: null,

  // Assessment creation/editing
  currentAssessment: null,
  assessmentStatus: REQUEST_STATUS.idle,
  assessmentError: null,

  // Survey creation/update status
  createSurveyStatus: REQUEST_STATUS.idle,
  createSurveyError: null,

  // Generated links and QR codes
  generatedLinks: {},
  qrCodes: {},
};

const instantAssessmentSlice = createSlice({
  name: 'instantAssessment',
  initialState,
  reducers: {
    // Instrument selection
    setInstantAssessmentInstrument: (state, action) => {
      state.selectedInstrument = action.payload;
    },

    // Current assessment
    setCurrentAssessment: (state, action) => {
      state.currentAssessment = action.payload;
    },
    clearCurrentAssessment: (state) => {
      state.currentAssessment = null;
    },

    // Links and QR codes
    setGeneratedLink: (state, action) => {
      const { assessmentId, link } = action.payload;
      state.generatedLinks[assessmentId] = link;
    },
    setQRCode: (state, action) => {
      const { assessmentId, qrCode } = action.payload;
      state.qrCodes[assessmentId] = qrCode;
    },

    resetState: () => initialState,
  },
  extraReducers: builder => {
    // Fetch Order
    builder
      .addCase(fetchInstantSurveys.pending, state => {
        state.instantSurveysStatus = REQUEST_STATUS.loading;
        state.instantSurveysError = null;
      })
      .addCase(fetchInstantSurveys.fulfilled, (state, action) => {
        state.instantSurveysStatus = REQUEST_STATUS.succeeded;
        state.instantSurveys = action.payload.data.data.instantSurveys;
        state.instantSurveysError = null;
      })
      .addCase(fetchInstantSurveys.rejected, (state, action) => {
        state.instantSurveysStatus = REQUEST_STATUS.failed;
        state.instantSurveysError = action.payload;
      })

      // Fetch surveys data
      .addCase(fetchInstantSurveysData.pending, (state) => {
        state.surveysStatus = REQUEST_STATUS.loading;
        state.surveysError = null;
      })
      .addCase(fetchInstantSurveysData.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.surveysStatus = REQUEST_STATUS.succeeded;
          state.assessmentInstruments = action.payload.data.instruments.filter(({isInstantSurvey}) => isInstantSurvey);
          state.surveysError = null;
        } else {
          state.surveysStatus = REQUEST_STATUS.failed;
          state.surveysError = action.payload.error?.message || 'Failed to load survey types';
        }
      })
      .addCase(fetchInstantSurveysData.rejected, (state, action) => {
        state.surveysStatus = REQUEST_STATUS.failed;
        state.surveysError = action.error.message || 'Failed to load survey types';
        state.assessmentInstruments = [];
      })

      // Create instant survey
      .addCase(createInstantSurvey.pending, (state) => {
        state.createSurveyStatus = REQUEST_STATUS.loading;
        state.createSurveyError = null;
      })
      .addCase(createInstantSurvey.fulfilled, (state, action) => {
        state.createSurveyStatus = REQUEST_STATUS.succeeded;
        state.instantSurveys = action.payload.data.data.instantSurveys;
        state.createSurveyError = null;
      })
      .addCase(createInstantSurvey.rejected, (state, action) => {
        state.createSurveyStatus = REQUEST_STATUS.failed;
        state.createSurveyError = action.payload?.error || 'Failed to create instant survey';
      })

      // Update instant survey
      .addCase(updateInstantSurvey.pending, (state) => {
        state.createSurveyStatus = REQUEST_STATUS.loading;
        state.createSurveyError = null;
      })
      .addCase(updateInstantSurvey.fulfilled, (state, action) => {
        state.createSurveyStatus = REQUEST_STATUS.succeeded;
        state.instantSurveys = action.payload.data.data.instantSurveys;
        state.createSurveyError = null;
      })
      .addCase(updateInstantSurvey.rejected, (state, action) => {
        state.createSurveyStatus = REQUEST_STATUS.failed;
        state.createSurveyError = action.payload?.error || 'Failed to create instant survey';
      });
  },
});

export const {
  fetchInstantSurveysStart,
  fetchInstantSurveysSuccess,
  fetchInstantSurveysFailure,
  setInstantAssessmentInstrument,
  setCurrentAssessment,
  clearCurrentAssessment,
  setGeneratedLink,
  setQRCode,
  resetState,
} = instantAssessmentSlice.actions;

export default instantAssessmentSlice.reducer;
